<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import PageNavigation from '@components/admin/page-navigation'
import { fetchBalanceList, createAdjust } from '@apis/admin/affiliate'
import { fetchAssets } from '@apis/admin/asset'
import { fetchList as fetchAccountList } from '@apis/admin/account'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'

export default {
  components: { PageNavigation },
  data() {
    return {
      loading: false,
      adjust_loading: false,
      list: [],
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      show_adjust_dialog: false,
      s_balance_info: null,
      s_account_seq: null,
      accounts: [],
      excel_headers: [
        ['seq', this.$t('affiliate_balance_list.seq')],
        ['member_seq', this.$t('affiliate_balance_list.member_seq')],
        ['user_id', this.$t('affiliate_balance_list.user_id')],
        ['user_name', this.$t('affiliate_balance_list.user_name')],
        ['email_address', this.$t('affiliate_balance_list.email_address')],
        ['amount', this.$t('affiliate_balance_list.amount')],
        ['total_amount', this.$t('affiliate_balance_list.total_amount')],
        ['asset_title', this.$t('affiliate_balance_list.asset_title')],
        ['asset_code', this.$t('affiliate_balance_list.asset_code')],
      ],
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
    show_adjust_dialog(to) {
      if (to) return
      this.s_balance_info = null
      this.s_account_seq = null
      this.accounts = []
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      const { data } = await fetchAssets()
      this.assets = data
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchBalanceList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadAccounts() {
      const { data } = await fetchAccountList({
        member_seq: this.s_balance_info.member_seq,
        asset_seq: this.s_balance_info.asset_seq,
        no_paging: 1,
      })

      this.accounts = data
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'market_seq')
      this.search()
    },
    date,
    fromPairs,
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_excel_warning'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchBalanceList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('affiliate_balance_list.allowance_balances_list', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleCreateAdjust(info) {
      this.loading = true
      try {
        this.s_balance_info = info
        await this.loadAccounts()
        this.show_adjust_dialog = true
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleSubmitAdjust() {
      this.adjust_loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_adjust_warning'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await createAdjust({
          balance_seq: this.s_balance_info.seq,
          account_seq: this.s_account_seq,
        })

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_adjust_success'),
          type: 'success',
        })

        this.show_adjust_dialog = false
        this.init()
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.adjust_loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('admin.use_statistics_exclude') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('admin.member_is_alive') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('asset') }}</div></th
            >
            <td>
              <select v-model="search_params.asset_seq">
                <option
                  v-for="{ seq, title } in [
                    { seq: undefined, title: $t('admin.total') },
                    ...assets,
                  ]"
                  :key="seq"
                  :value="seq"
                  >{{ title }}</option
                >
              </select>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['balance', $t('wallet.balance')],
                    ['holding_balance', $t('wallet.hold_balance')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin.balance_by_asset') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col" rowspan="2"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('admin.asset_title') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.modal_asset_code') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('wallet.balance') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('affiliate_balance_list.total_amount') }}</div></th
          >
          <th scope="col"><div>Action</div></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(data, idx) in list">
          <tr :key="`1-${data.seq}`" :class="$style[`bg${idx % 2}`]">
            <td :class="[$style.tCenter, $style.nowrap]">{{ data._no }}</td>
            <template v-if="!$route.params.member_seq">
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_id
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_name
              }}</td>
              <td :class="[$style.nowrap]">{{ data.email_address }}</td>
            </template>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.asset_title
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.asset_code
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">{{
              data.amount | trimDecimalZero | numberFormat
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">{{
              data.total_amount | trimDecimalZero | numberFormat
            }}</td>
            <td
              :class="[$style.tCenter, $style.nowrap]"
              @click.prevent="handleCreateAdjust(data)"
            >
              <a href="#" :class="[$style.button, $style.small]"
                ><span>{{ $t('admin.adjust_success') }}</span></a
              >
            </td>
          </tr>
        </template>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 6 : 9"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 6 : 9"
            :class="$style.tCenter"
            >{{ $t('admin.no_search_account') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
    <ElDialog
      :title="$t('admin.allowance_adjust_success')"
      :visible.sync="show_adjust_dialog"
      :close-on-click-modal="!adjust_loading"
      :close-on-press-escape="!adjust_loading"
      :show-close="!adjust_loading"
    >
      <form
        v-if="show_adjust_dialog"
        v-loading="adjust_loading"
        @submit.prevent="handleSubmitAdjust"
      >
        <div :class="[$style.tCenter]">
          <i18n path="affiliate_balance_list.msg_adjust_to_account">
            <template>{{ s_balance_info.user_id }}</template>
            <template>{{ s_balance_info.asset_title }}</template>
            <template>{{
              s_balance_info.amount | trimDecimalZero | numberFormat
            }}</template>
          </i18n>
        </div>
        <div :class="[$style.tCenter]">
          <p style="margin: 10px 0;"
            ><BaseIcon name="arrow-down" size="3x"></BaseIcon
          ></p>
          <select v-model="s_account_seq">
            <option v-if="accounts.length" :value="null">{{
              $t('admin.msg_account_select')
            }}</option>
            <option v-else :value="null" :disabled="true">{{
              $t('admin.msg_account_select_no')
            }}</option>
            <option
              v-for="account in accounts"
              :key="account.seq"
              :value="account.seq"
            >
              <template v-if="account.title !== null && account.title.length">
                {{ account.title }} ({{ account.account_number }})
              </template>
              <template v-else>{{ account.account_number }}</template>
            </option>
          </select>
        </div>
        <div :class="[$style.buttonArea]">
          <span :class="[$style.button, $style.black, $style.xLarge]"
            ><input type="submit" :value="$t('admin.execution')" accesskey="s"
          /></span>
        </div>
      </form>
    </ElDialog>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
